// @<COPYRIGHT>@
// ==================================================
// Copyright (c) 2020 Siemens
// Siemens Product Lifecycle Management Software Inc.
// All Rights Reserved.
// ==================================================
// @<COPYRIGHT>@

/**
 * This module provides workspace preview.
 *
 * @module js/swf3dWorkspacePreview
 */

import utils from 'js/swf3dWorkspaceUtils';
import dataset from 'js/swf3dWorkspaceDataset';
import config from 'js/swf3dWorkspaceConfig';
import { http } from '@swf3d/utils';

var swf3dWorkspacePreview = {};

/**
 * Interface for get preview image string.
 *
 * @memberof js/swf3dWorkspacePreview
 * @function createFolder
 * @param {string} type - the type of object to get a preview from
 * @param {string} urn - the identifier of the specific object to read the preview from
 * @example
 * swf3dWorkspacePreview.getPreviewImage('Folder', '');
 * @returns {Promise<string>} the URL to use for the (preview) image
 */
swf3dWorkspacePreview.getPreviewImage = function (type, urn) {
    return new Promise((resolve, reject) => {
        if (type === 'Project') {
            resolve('assets/image/swf3dProject64.png');
        } else if (type === 'Folder') {
            resolve('assets/image/swf3dFolder.png');
        } else if (!utils.checkType(type)) {
            resolve('assets/image/swf3dIssue64.png');
        } else if (config.showPreview === false || config.storage !== 's3') {
            resolve('assets/image/swf3dPart48.png');
        } else {
            // Get image data from cache.
            if (utils.isPresent(dataset.previewCache[urn])) {
                resolve(dataset.previewCache[urn]);
            } else {
                swf3dWorkspacePreview
                    .getPreviewFromService(urn)
                    .then(previewRes => {
                        resolve(previewRes);
                    })
                    .catch(previewErr => {
                        resolve('assets/image/swf3dPart48.png');
                    });
            }
        }
    });
};

/**
 * Method for get preview input data.
 *
 * @memberof js/swf3dWorkspacePreview
 * @function getPreviewInputData
 * @param {string} urn - part urn
 * @example
 * swf3dWorkspacePreview.getPreviewInputData('123456');
 * @returns {object} Atlas preview API
 */
swf3dWorkspacePreview.getPreviewInputData = function (urn) {
    // Prepare input data for get preview image.
    let inputData = {
        API: [
            {
                AWS: {
                    region: config.region,
                    bucket: config.bucket,
                    accessKey: config.accessKey,
                    secretKey: config.secretKey
                },
                Class: 'SPLM.AtlasSession'
            },
            {
                Class: 'SPLM.QuickAccessFolders',
                GetPreview: {
                    filename: urn
                }
            }
        ]
    };

    return inputData;
};

/**
 * Method for get preview HTTP options.
 * For sending http requests.
 *
 * @memberof js/swf3dWorkspacePreview
 * @function getPreviewInputData
 * @param {string} urn - part urn
 * @example
 * swf3dWorkspacePreview.getPreviewHttpOptions('123456');
 * @returns {object} Atlas HTTP options
 */
swf3dWorkspacePreview.getPreviewHttpOptions = function (urn) {
    // Prepare input data for get preview image.
    let inputData = swf3dWorkspacePreview.getPreviewInputData(urn);

    let options = {
        url: config.previewHost,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        data: inputData,
        timeout: 3000
    };

    return options;
};

/**
 * Get preview image from preview service by url.
 *
 * @memberof js/swf3dWorkspacePreview
 * @function getPreviewFromService
 * @param {string} urn - part urn
 * @example
 * swf3dWorkspacePreview.getPreviewFromService('123456');
 * @returns {Promise} Preview image data.
 */
swf3dWorkspacePreview.getPreviewFromService = function (urn) {
    const { url, data, ...options } = swf3dWorkspacePreview.getPreviewHttpOptions(urn);
    return http.post(url, data, options).then(res => {
        let result = res.API_RESULT;
        if (
            utils.isPresent(result) &&
            result.length > 0 &&
            utils.isPresent(result[0]) &&
            utils.isPresent(result[0].GetPreview) &&
            utils.isPresent(result[0].GetPreview.preview)
        ) {
            let image = 'data:image/png;base64,' + result[0].GetPreview.preview;
            // Save image to cache.
            dataset.previewCache[urn] = image;
            return image;
        }
        return Promise.reject('Get image fail');
    });
};

export default swf3dWorkspacePreview;
